// Per-character financial breakdown. One row per contract; status pulled
// from the contract row, line counts from the character row.

import { Badge, Button } from '@ohear/ui'
import type { CharacterRow, ContractRow, ContractStatus } from '../../lib/api/producer-types'

const STATUS_VARIANT: Record<ContractStatus, 'gray' | 'blue' | 'green' | 'amber' | 'red'> = {
  draft: 'gray',
  sent: 'amber',
  signed: 'blue',
  active: 'green',
  completed: 'green',
  voided: 'red',
}

export interface CharacterFinancialsTableProps {
  characters: CharacterRow[]
  contracts: ContractRow[]
  /** Called with the contract id when "Trigger payout" is clicked. */
  onTriggerPayout: (contractId: string) => void
  busyContractId?: string | null
}

export function CharacterFinancialsTable({
  characters,
  contracts,
  onTriggerPayout,
  busyContractId,
}: CharacterFinancialsTableProps) {
  const charById = new Map(characters.map((c) => [c.id, c] as const))
  return (
    <div
      className="overflow-x-auto rounded-[var(--radius-lg)] border border-[var(--border)]"
      style={{ background: 'var(--bg-2)' }}
    >
      <table className="w-full text-[13px] border-separate" style={{ borderSpacing: 0 }}>
        <thead
          className="sticky top-0 z-10 text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]"
          style={{ background: 'var(--bg-3)' }}
        >
          <tr>
            <th className="px-3 py-2 font-medium">Character</th>
            <th className="px-3 py-2 font-medium text-right">Lines</th>
            <th className="px-3 py-2 font-medium text-right">Contract value</th>
            <th className="px-3 py-2 font-medium text-right">Paid</th>
            <th className="px-3 py-2 font-medium text-right">Outstanding</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Action</th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((contract) => {
            const character = charById.get(contract.character_id)
            return (
              <tr key={contract.id} className="transition-colors hover:bg-[var(--bg-3)]">
                <td
                  className="px-3 py-2 text-[var(--text)] font-medium border-t"
                  style={{ borderColor: 'var(--border)' }}
                >
                  {character?.display_name ?? contract.character_id}
                </td>
                <td
                  className="px-3 py-2 text-right tabular-nums text-[var(--text)] border-t"
                  style={{ borderColor: 'var(--border)' }}
                >
                  {character?.line_count ?? 0}
                </td>
                <td
                  className="px-3 py-2 text-right tabular-nums text-[var(--text)] border-t"
                  style={{ borderColor: 'var(--border)' }}
                >
                  {formatUsd(contract.total_value_usd)}
                </td>
                <td
                  className="px-3 py-2 text-right tabular-nums text-[var(--green)] border-t"
                  style={{ borderColor: 'var(--border)' }}
                >
                  {formatUsd(contract.paid_usd)}
                </td>
                <td
                  className="px-3 py-2 text-right tabular-nums border-t"
                  style={{
                    borderColor: 'var(--border)',
                    color: contract.outstanding_usd > 0 ? 'var(--amber)' : 'var(--text-3)',
                  }}
                >
                  {formatUsd(contract.outstanding_usd)}
                </td>
                <td className="px-3 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
                  <Badge variant={STATUS_VARIANT[contract.status]}>{contract.status}</Badge>
                </td>
                <td className="px-3 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
                  <Button
                    size="sm"
                    variant={contract.outstanding_usd > 0 ? 'primary' : 'ghost'}
                    disabled={contract.outstanding_usd <= 0 || contract.status !== 'active'}
                    loading={busyContractId === contract.id}
                    onClick={() => onTriggerPayout(contract.id)}
                  >
                    Trigger payout
                  </Button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function formatUsd(value: number | null | undefined): string {
  // Financials can be absent before the first contract lands — render an
  // honest em-dash instead of "$NaN" (Intl happily formats undefined to NaN).
  if (value == null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}
